import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const ToggleSetting = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between rounded-lg bg-zinc-800/50 p-4">
    <div>
      <label className="font-medium text-white">{label}</label>
      <p className="text-xs text-zinc-400">{description}</p>
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`h-6 w-12 rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-zinc-600'}`}
    >
      <div className={`h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-0.5'}`} />
    </button>
  </div>
);

const WorkflowSettingsPanel = ({ token }) => {
  const [settings, setSettings] = useState({
    enable_system_automations: true,
    enable_password_reset_emails: true,
    enable_email_confirmation_emails: true,
    enable_agency_onboarding_emails: true,
    enable_ticket_notification_emails: true,
    enable_fallback_sender_for_agency: true,
    default_retry_attempts: 3,
    token_expiration_minutes: 60,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/platform/system-automations/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/platform/system-automations/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflow-settings-panel">
      <Card className="border-zinc-800 bg-zinc-900/50">
        <CardHeader>
          <CardTitle className="text-white">Global Automation Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleSetting
            label="Enable System Automations"
            description="Master switch for all platform email automations"
            checked={settings.enable_system_automations}
            onChange={(v) => setSettings({ ...settings, enable_system_automations: v })}
          />
          <ToggleSetting
            label="Password Reset Emails"
            description="Send password reset emails when requested"
            checked={settings.enable_password_reset_emails}
            onChange={(v) => setSettings({ ...settings, enable_password_reset_emails: v })}
          />
          <ToggleSetting
            label="Email Confirmation Emails"
            description="Send email verification links"
            checked={settings.enable_email_confirmation_emails}
            onChange={(v) => setSettings({ ...settings, enable_email_confirmation_emails: v })}
          />
          <ToggleSetting
            label="Agency Onboarding Emails"
            description="Send welcome and invite emails for agencies"
            checked={settings.enable_agency_onboarding_emails}
            onChange={(v) => setSettings({ ...settings, enable_agency_onboarding_emails: v })}
          />
          <ToggleSetting
            label="Ticket Notification Emails"
            description="Send ticket created, replied, and status emails"
            checked={settings.enable_ticket_notification_emails}
            onChange={(v) => setSettings({ ...settings, enable_ticket_notification_emails: v })}
          />
          <ToggleSetting
            label="Platform Fallback for Agency Emails"
            description="Use Platform Mailgun when Agency Mailgun unavailable"
            checked={settings.enable_fallback_sender_for_agency}
            onChange={(v) => setSettings({ ...settings, enable_fallback_sender_for_agency: v })}
          />

          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Default Retry Attempts</label>
              <Input
                type="number"
                min="1"
                max="10"
                value={settings.default_retry_attempts}
                onChange={(e) => setSettings({ ...settings, default_retry_attempts: parseInt(e.target.value, 10) || 3 })}
                className="border-zinc-700 bg-zinc-800 text-white"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Token Expiration (minutes)</label>
              <Input
                type="number"
                min="15"
                max="1440"
                value={settings.token_expiration_minutes}
                onChange={(e) => setSettings({ ...settings, token_expiration_minutes: parseInt(e.target.value, 10) || 60 })}
                className="border-zinc-700 bg-zinc-800 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 text-white hover:bg-emerald-600">
          {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default WorkflowSettingsPanel;
